/**
 * 푸터 링크 SSoT — 커머스 AppFooter(다크)와 워크스페이스 WorkspaceFooter(라이트)가 같은 컬럼·항목을 쓴다.
 * 항목을 고칠 땐 여기만 고친다(두 푸터에 재기입 금지).
 *
 * ⚠️ 프리렌더 대상(공개·색인) 경로는 trailing slash 정규형으로 링크한다 — 크롤러가 푸터 링크를
 * 전 페이지에서 따라가므로, 슬래시가 빠지면 매 링크가 301 을 한 번씩 태운다(근거: brand.ts canonicalUrl).
 * 비색인 경로(/mypage/* 등)는 robots Disallow 라 대상이 아니다.
 */

/** 라우트 이동 항목. */
export interface FooterRouteLink {
  label: string;
  to: string;
}

/** 커머스 랜딩(`/`) 섹션 앵커 항목 — useScrollTo.goToSection('/', hash) 로 이동. */
export interface FooterHashLink {
  label: string;
  hash: string;
}

export type FooterLink = FooterRouteLink | FooterHashLink;

/** 푸터 컬럼 1개. */
export interface FooterColumn {
  title: string;
  links: readonly FooterLink[];
}

export function isRouteLink(link: FooterLink): link is FooterRouteLink {
  return 'to' in link;
}

const reportLinks: readonly FooterLink[] = [
  { label: '자료 탐색', to: '/materials/' },
  { label: '리포트 샘플', hash: '#sample' },
  // 면접 로드맵 — 구매 화면 대신 랜딩 소개 섹션(판매 중지 시 404 회피, 근거는 nav.ts 같은 항목).
  { label: '면접 로드맵', hash: '#product-interview' },
  { label: '가격 안내', hash: '#price' },
];

const supportLinks: readonly FooterLink[] = [
  { label: '자주 묻는 질문', hash: '#faq' },
  { label: '내 자료실', to: '/mypage/library' },
  { label: '1:1 문의', to: '/mypage/inquiries' },
];

/**
 * 가이드 발견 링크 — 검색 유입 자산의 크롤 진입점(B-13: 헤더 미연결·푸터 발견 링크만).
 * 주제형 3편은 허브(`/guide`)가 목록을 소유하므로 대표로 허브 1건만 노출한다.
 * 대학형(`/guide/yonsei` 등)은 라우트·페이지는 유지하되 푸터 링크에서 제외 —
 * 진열 샘플이 바뀔 때마다 대학 정보를 갱신하기 어려워 운영 부담이 크다.
 */
const guideLinks: readonly FooterLink[] = [
  { label: '입시 가이드 전체', to: '/guide/' },
  { label: '입시 소식', to: '/news/' },
  { label: '수시 6회 지원 규정', to: '/guide/susi-6-rule/' },
  { label: '고3 수시 지원 전략', to: '/guide/susi-strategy/' },
  { label: '면접 준비 전략', to: '/guide/interview/' },
  { label: '자기소개서 작성법', to: '/guide/self-introduction/' },
];

/** 푸터 컬럼 3종 — 리포트 · 고객 지원 · 입시 가이드(순서 고정). */
export const FOOTER_COLUMNS: readonly FooterColumn[] = [
  { title: '리포트', links: reportLinks },
  { title: '고객 지원', links: supportLinks },
  { title: '입시 가이드', links: guideLinks },
];

/** 법적 고지 링크 — 사업자 정보 아래 한 줄. */
export const FOOTER_LEGAL_LINKS: readonly FooterRouteLink[] = [
  { label: '이용약관', to: '/legal/terms/' },
  { label: '개인정보처리방침', to: '/legal/privacy/' },
  { label: '사업자정보', to: '/legal/business/' },
];
